import { type AlertProps, Badge, Box, Flex, Tooltip } from "@chakra-ui/react";
import { dayjs } from "@equidefi/i18n";
import { PAYMENT_STRIKETHROUGH_AMOUNT_STATUSES } from "@equidefi/shared/constants/payments";
import type { DTO } from "@equidefi/shared/types/dto";
import { BannerAlert, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";
import type React from "react";

import { CurrencyFormatter } from "@/constants/format";
import { PAYMENT_STATUS_DISPLAY } from "@/constants/payments";

export const PAYMENT_TYPE_CONFIG = {
  wire: { label: "Wire Transfer", icon: Icon.DollarSign },
  customer_balance: { label: "Wire Transfer", icon: Icon.DollarSign },
  card: { label: "Credit Card Payment", icon: Icon.CreditCard },
  us_bank_account: { label: "ACH Payment", icon: Icon.Archive },
  cashapp: { label: "Cashapp", icon: Icon.CreditCard },
  check: { label: "Check Payment", icon: Icon.Mail },
  crypto: { label: "Crypto Payment", icon: Icon.Layers },
};

/**
 * PaymentStatusBadge — status pill with a tooltip describing the status.
 */
export const PaymentStatusBadge: React.FC<{ payment: DTO.Payment }> = ({ payment }) => {
  const config = PAYMENT_STATUS_DISPLAY[payment.status];
  if (!config) return null;

  const title = typeof config.title === "function" ? config.title(payment) : config.title;
  const description = typeof config.description === "function" ? config.description(payment) : config.description;

  return (
    <Tooltip label={description}>
      <Badge
        fontSize={{ base: "0.65em", md: "0.75em" }}
        px="3"
        py="1"
        borderRadius="full"
        textTransform="uppercase"
        variant="solid"
        colorScheme={config.color}>
        {title}
      </Badge>
    </Tooltip>
  );
};

/**
 * PaymentInfoCard — colored banner (info / error) describing the most recent payment
 */
export const PaymentInfoCard: React.FC<
  { payment: DTO.Payment; title?: string; description?: React.ReactNode } & AlertProps
> = ({ payment, title, description, ...props }) => {
  const config = PAYMENT_STATUS_DISPLAY[payment.status];
  if (!config) return null;

  const statusTitle = typeof config.title === "function" ? config.title(payment) : config.title;
  const statusDescription = typeof config.description === "function" ? config.description(payment) : config.description;
  const strikethrough =
    Boolean(payment.is_expired) || PAYMENT_STRIKETHROUGH_AMOUNT_STATUSES.some((s) => s === payment.status);

  return (
    <BannerAlert
      {...props}
      status={config.status as "success" | "warning" | "error" | "info"}
      title={title ?? statusTitle}
      description={
        <Box>
          <Text m="0" mb="4">
            {description ?? statusDescription}
          </Text>
          <Text fontSize="3xl" fontWeight="bold" m="0" textDecoration={strikethrough ? "line-through" : "none"}>
            {CurrencyFormatter.format(Number(payment.amount))}
          </Text>
          <Flex w="full" justify="space-between" align="center" flexWrap="wrap" gap="2" mt="1">
            <Text fontSize="md" m="0">
              {PAYMENT_TYPE_CONFIG[payment.payment_type]?.label ?? payment.payment_type}
            </Text>
            <PaymentStatusBadge payment={payment} />
          </Flex>
          <Text fontSize="sm" color="gray.500" m="0" mt="1">
            {dayjs(payment.create_date).fromNow()}
          </Text>
        </Box>
      }
    />
  );
};
